import { useEffect, useState } from "react";

/**
 * Loads every image before the site becomes interactive.
 *
 * This matters more than it looks: if the lit frames aren't already in
 * memory, the first click on the switch shows a blank flash while the
 * browser fetches them — which kills the one moment the whole site exists
 * for. Better to wait 2 seconds up front than to stutter on the reveal.
 */
export default function usePreload(sources) {
  const [loaded, setLoaded] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let done = 0;

    if (!sources.length) {
      setReady(true);
      return;
    }

    sources.forEach((src) => {
      const img = new Image();
      img.decoding = "async";
      img.src = src;

      const finish = () => {
        if (cancelled) return;
        done += 1;
        setLoaded(done);
        if (done === sources.length) {
          // brief hold so the counter doesn't flash past
          setTimeout(() => !cancelled && setReady(true), 260);
        }
      };

      if (img.complete) finish();
      else img.onload = img.onerror = finish;
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ready, progress: sources.length ? loaded / sources.length : 1 };
}
